import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { sv } from "date-fns/locale";
import PaymentStatusBadge from "@/components/booking/PaymentStatusBadge";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";

export default function PaymentHistory() {
  const { user } = useAuth();

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ["my-payment-history", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_payments")
        .select("*, bookings(start_time, courts(name))")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });

  // Calculate monthly total — Sprint D aggregates: prefer amount_minor.
  const now = new Date();
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);
  const monthlyPaidMinor = payments
    .filter((p: any) =>
      ["paid", "manual"].includes(p.status) &&
      p.paid_at &&
      new Date(p.paid_at) >= monthStart &&
      new Date(p.paid_at) <= monthEnd
    )
    .reduce((sum: number, p: any) => sum + (resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0), 0);
  const monthlyPaidMoney = { valueMinor: monthlyPaidMinor, currency: "SEK" as const };

  if (isLoading) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-muted-foreground">Betalt denna månad</span>
            <span className="text-xl font-bold">{formatPrice(monthlyPaidMoney)}</span>
          </div>
        </CardContent>
      </Card>

      {payments.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Ingen betalningshistorik</p>
      ) : (
        <div className="space-y-2">
          {payments.map((p: any) => (
            <Card key={p.id}>
              <CardContent className="p-3 flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">
                    {(p as any).bookings?.courts?.name ?? "Bokning"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {(p as any).bookings?.start_time
                      ? format(new Date((p as any).bookings.start_time), "d MMM yyyy HH:mm", { locale: sv })
                      : format(new Date(p.created_at), "d MMM yyyy", { locale: sv })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{formatPrice(resolveMoney(p.amount_minor, p.amount, p.currency) ?? { valueMinor: 0, currency: "SEK" })}</span>
                  <PaymentStatusBadge status={p.status} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
